/**
 * AttachmentThumbnail - Componente compartilhado de miniatura de anexo
 * 
 * Usado nos módulos CRM (Deals) e Tarefas para exibir
 * miniaturas de arquivos com overlay de ações (download/excluir).
 * 
 * Versão: 1.1.0
 * RELEASE: 2026-04-06 v1.0.0 - Extração de componente duplicado
 * RELEASE: 2026-08-21 v1.1.0 - UX: overlay de hover mantém somente o botão
 *          baixar (centralizado). O botão excluir vira um "X" em bolinha no
 *          canto superior direito, evitando exclusão acidental ao tentar
 *          abrir o preview do anexo.
 */
import React, { useCallback } from 'react';
import { Download, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getFileIcon, getFileColor, formatFileSize } from '@/lib/file-utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

/** Interface genérica compatível com DealAttachment e TaskAttachment */
export interface BaseAttachment {
  id: string;
  file_name: string;
  file_type: string;
  public_url: string;
  thumbnail_url: string | null;
  file_size: number | null;
}

/** Props do componente */
interface AttachmentThumbnailProps {
  /** Dados do anexo */
  attachment: BaseAttachment;
  /** Callback ao excluir */
  onDelete?: () => void;
  /** Modo somente leitura (oculta botão de excluir) */
  readOnly?: boolean;
  /** Callback ao clicar na miniatura */
  onClick?: () => void;
  /** Classes CSS adicionais para o container (ex: tamanho customizado) */
  className?: string;
}

/**
 * Miniatura de anexo com overlay de ações no hover
 * Exibe imagem (thumbnail) ou ícone baseado no tipo de arquivo
 */
export function AttachmentThumbnail({
  attachment,
  onDelete,
  readOnly,
  onClick,
  className,
}: AttachmentThumbnailProps) {
  const FileIcon = getFileIcon(attachment.file_type);
  const colorClasses = getFileColor(attachment.file_type);
  const isImage = attachment.file_type === 'image';

  /** Download programático via fetch+blob (contorna limitação cross-origin do atributo download) */
  const handleDownload = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await fetch(attachment.public_url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Erro ao baixar arquivo');
    }
  }, [attachment.public_url, attachment.file_name]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'group relative w-16 h-16 rounded-lg border bg-background overflow-hidden flex items-center justify-center cursor-pointer',
              className
            )}
            onClick={onClick}
          >
            {/* Imagem ou ícone */}
            {isImage && (attachment.thumbnail_url || attachment.public_url) ? (
              <img
                src={attachment.thumbnail_url || attachment.public_url}
                alt={attachment.file_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className={cn('p-2 rounded', colorClasses)}>
                <FileIcon className="h-6 w-6" />
              </div>
            )}

            {/* Overlay com ação de download no hover (centralizado) */}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <button
                onClick={handleDownload}
                className="p-1.5 rounded bg-white/20 hover:bg-white/30 transition-colors"
                title="Download"
              >
                <Download className="h-3.5 w-3.5 text-white" />
              </button>
            </div>

            {/* v1.1.0: Excluir isolado no canto superior direito (evita clique acidental) */}
            {!readOnly && onDelete && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete();
                }}
                className="absolute top-0.5 right-0.5 z-10 h-5 w-5 rounded-full bg-destructive text-destructive-foreground shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:brightness-110"
                title="Remover"
                aria-label="Remover anexo"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[200px]">
          <p className="font-medium text-xs truncate">{attachment.file_name}</p>
          {attachment.file_size && (
            <p className="text-[10px] text-muted-foreground">
              {formatFileSize(attachment.file_size)}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
